import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { UserAvatar } from "@/components/UserAvatar";

/**
 * Cursor personalizado: flecha normal + pequeño círculo con avatar del usuario.
 * Solo se muestra si la opción "Mostrar cursores" está activa y hay usuario logueado.
 */
export function CustomCursor() {
  const { user } = useAuth();
  const { showCursors } = useSettings();
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [visible, setVisible] = useState(false);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!showCursors || !user) return;

    const onMouseMove = (e) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setPos({ x: e.clientX, y: e.clientY });
        setVisible(true);
        document.body.style.cursor = "none";
      });
    };
    const onMouseLeave = () => {
      setVisible(false);
      document.body.style.cursor = "";
    };

    window.addEventListener("mousemove", onMouseMove, { passive: true });
    document.documentElement.addEventListener("mouseleave", onMouseLeave);

    return () => {
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", onMouseMove);
      document.documentElement.removeEventListener("mouseleave", onMouseLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [showCursors, user]);

  if (!showCursors || !user || !visible) return null;

  return (
    <div
      className="pointer-events-none fixed z-[10000]"
      style={{
        left: pos.x,
        top: pos.y,
        transform: "translate(2px, 2px)",
      }}
      aria-hidden
    >
      {/* Flecha tipo cursor (puntero arriba-izquierda) */}
      <svg
        width="20"
        height="24"
        viewBox="0 0 20 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="drop-shadow-md"
      >
        <path
          d="M4 4L4 18L9 13L13 21L15 20L11 12L18 10L4 4Z"
          fill="white"
          stroke="black"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
      </svg>
      {/* Círculo con avatar, pequeño y desplazado para no tapar la flecha */}
      <div
        className="absolute rounded-full ring-[1.5px] ring-background shadow-sm overflow-hidden"
        style={{
          width: 14,
          height: 14,
          left: 12,
          top: -6,
        }}
      >
        <UserAvatar user={user} size={14} className="block w-full h-full" />
      </div>
    </div>
  );
}
